import styled from '@emotion/styled/macro'
import { AutoStack, LocalDate } from 'components'
import { ResourceManager } from 'components/TimeLine'
import { ProvingUserRole, UserRole } from 'gql.generated'
import { Box } from 'grid'
import { isInProvingRole } from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { themeColor } from 'mixins'
import {
  ActionButton,
  DefaultButton,
  Icon,
  Link,
  Panel,
  PanelType,
  Stack,
  Text,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { getEstimatedCost } from './CostEstimateHelper'
import { useUpdateProvingMutation } from './hooks/ProvingDetailsView.generated'

enum FormType {
  getResourceManager,
}

const Container = styled(Box)`
  /* background: repeating-linear-gradient(
    ${(props: { angle?: number }) => props.angle || -45}deg,
    ${themeColor('neutralLighter')},
    ${themeColor('neutralLighter')} 10px,
    ${themeColor('neutralLighterAlt')} 10px,
    ${themeColor('neutralLighterAlt')} 20px
  ); */
  /* border-bottom: 1px solid ${themeColor('neutralLighterAlt')}; */
  box-shadow: 0 1.6px 3.6px 0 rgba(0, 0, 0, 0.132),
    0 0.3px 0.9px 0 rgba(0, 0, 0, 0.108);
`

export interface ProvingHeaderProps {
  onShowPendingChanges?: () => void
}

// TODO: i18n
export const ProvingHeader: React.FC<ProvingHeaderProps> = ({
  onShowPendingChanges: onShowChanges,
}) => {
  const { t } = useTranslation('ProvingDetailsView')
  const [proving, refetch] = useCurrentProving()
  const [updateProving] = useUpdateProvingMutation()
  const [, showPanel] = useFormikPanelState()
  const [formState, setFormState] = React.useState()
  const [showAllResponsible, setShowAllResponsible] = React.useState(false)
  const auth = useAuthContext()
  const authUserId = auth && auth.user && auth.user._id
  const isAdmin = auth.hasUserRole(UserRole.SystemAdministrator)
  const isResourceManager =
    auth.hasUserRole(UserRole.ResourceManager) ||
    isInProvingRole(proving, ProvingUserRole.ResourceManager)
  const isHRG = auth.hasUserRole(UserRole.Hrg)
  const serviceTeam = auth.hasUserRole(UserRole.ServiceTeam)
  const isProvingOrganisator = isInProvingRole(proving, ProvingUserRole.Editor)
  const serviceTeamPermissions =
    proving &&
    proving.provingGround &&
    proving.provingGround.authorizedEditors &&
    proving.provingGround.authorizedEditors.find(
      (ae: any) => ae._id === authUserId
    )
  const isServiceTeam = serviceTeam && serviceTeamPermissions ? true : false

  let count = 0
  const calculateCosts = React.useCallback(async () => {
    const costs = getEstimatedCost(proving)
    // if (proving && proving.resourceBookings) {
    //   proving.resourceBookings.forEach(resource => {
    //     if (resource.resource && resource.resource.costPerUnit) {
    //       const latestDate: Date = new Date(resource.dateTo)
    //       const lowestDate: Date = new Date(resource.dateFrom)
    //       const diffTime: any = Math.abs(
    //         latestDate.getTime() - lowestDate.getTime()
    //       )
    //       const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    //       if (
    //         (resource.usageTime &&
    //           resource.resource &&
    //           resource.resource.type === ResourceSelectionType.TestTrack) ||
    //         (resource.usageTime &&
    //           resource.resource &&
    //           resource.resource.type === ResourceSelectionType.TestTrackUsage)
    //       ) {
    //         costs +=
    //           resource.resource.costPerUnit *
    //           (diffDays + 1) *
    //           resource.usageTime
    //       } else {
    //         costs += resource.resource.costPerUnit * (diffDays + 1)
    //       }
    //       costs = Math.round(costs * 100) / 100
    //     }
    //   })
    // }

    if (proving.sumOfCosts !== costs && count === 0) {
      await updateProving({
        variables: {
          id: proving.id,
          update: {
            sumOfCosts: costs,
          },
        },
      })
      count++
    }
    // refetch()
  }, [proving, updateProving, refetch, count])
  calculateCosts()

  return (
    <Container px={4} pt={2} pb={2} mb="3px">
      <AutoStack horizontalAlign="space-between" horizontal={true}>
        <AutoStack horizontal={true} tokens={{ childrenGap: 12 }}>
          <AutoStack styles={{ root: { marginRight: 50 } }}>
            <Text variant="xLarge" as="h2">
              {proving.id}
            </Text>
            <AutoStack horizontal={true} horizontalAlign="space-between">
              <Stack
                horizontal={true}
                verticalAlign="center"
                tokens={{ childrenGap: 3 }}
              >
                <Stack.Item>
                  <Icon iconName="calendar" />
                  &nbsp;
                </Stack.Item>
                <Stack.Item>
                  <LocalDate graphQLDate={proving.dateFrom} />
                </Stack.Item>
                <Stack.Item>&mdash;</Stack.Item>
                <Stack.Item>
                  <LocalDate graphQLDate={proving.dateTo} />
                </Stack.Item>
              </Stack>
            </AutoStack>
            <Stack
              horizontal={true}
              verticalAlign="center"
              tokens={{ childrenGap: 3 }}
            >
              <Stack.Item>
                <Icon iconName="List" />
                &nbsp;
              </Stack.Item>
              <Stack.Item>
                <Text variant="medium" as="h4">
                  {proving.title}
                </Text>
              </Stack.Item>
            </Stack>
            {(isAdmin || isResourceManager) && proving.sumOfCosts !== 0 && (
              <Stack
                horizontal={true}
                verticalAlign="center"
                tokens={{ childrenGap: 3 }}
              >
                <Stack.Item>
                  <Icon iconName="Money" />
                  &nbsp;
                </Stack.Item>
                <Stack.Item>
                  <Text>
                    Summe der Kosten:{' '}
                    {proving.sumOfCosts ? proving.sumOfCosts.toFixed(2) : 0} €
                  </Text>
                </Stack.Item>
              </Stack>
            )}
          </AutoStack>
          <AutoStack styles={{ root: { paddingTop: 5, marginRight: 50 } }}>
            {proving &&
              proving.responsible &&
              proving.responsible.length > 0 &&
              proving.responsible
                .slice(0, showAllResponsible ? proving.responsible.length : 5)
                .map((r, i) => (
                  <Stack
                    horizontal={true}
                    verticalAlign="center"
                    tokens={{ childrenGap: 3 }}
                    key={i}
                  >
                    <Stack.Item>
                      <Icon iconName="EditNote" />
                      &nbsp;
                    </Stack.Item>
                    <Stack.Item>{r.name}</Stack.Item>
                  </Stack>
                ))}
            {proving &&
              proving.responsible &&
              proving.responsible.length > 5 && (
                <ActionButton
                  iconProps={{
                    iconName: showAllResponsible ? 'ChevronUp' : 'ChevronDown',
                  }}
                  onClick={() => setShowAllResponsible(!showAllResponsible)}
                >
                  {showAllResponsible ? 'Weniger anzeigen' : 'Alle anzeigen'}
                </ActionButton>
              )}
          </AutoStack>
          <AutoStack styles={{ root: { paddingTop: 5 } }}>
            <Stack
              horizontal={true}
              verticalAlign="center"
              tokens={{ childrenGap: 3 }}
            >
              <Stack.Item>
                <Text>{proving.department ? proving.department : ''}</Text>
              </Stack.Item>
            </Stack>
          </AutoStack>
        </AutoStack>
        <AutoStack
          styles={{ root: { paddingTop: 5 } }}
          horizontal={true}
          tokens={{ childrenGap: 12 }}
        >
          {proving.pendingChanges &&
            proving.pendingChanges.length > 0 &&
            (isAdmin ||
              isResourceManager ||
              isServiceTeam ||
              isProvingOrganisator) && (
              <div>
                <Link onClick={onShowChanges}>
                  {t('header.pendingChanges', {
                    count: proving.pendingChanges.length,
                  })}
                </Link>
              </div>
            )}

          <DefaultButton
            hidden={!!isHRG}
            onClick={() => {
              showPanel({
                status: proving.status,
              })
              setFormState({ formType: FormType.getResourceManager })
            }}
          >
            Applet
          </DefaultButton>
        </AutoStack>
      </AutoStack>
      {formState !== undefined && (
        <Panel
          type={PanelType.extraLarge}
          headerText={t('panel.resourceManager.title')}
          closeButtonAriaLabel={t('action.close')}
          isLightDismiss={true}
          onDismiss={() => setFormState({ formType: undefined })}
          isOpen={formState.formType === FormType.getResourceManager}
        >
          <ResourceManager label={'Label'} />
        </Panel>
      )}
    </Container>
  )
}
